import React, { useState } from "react";

import { useIntl } from "react-intl";
import { useDispatch, useSelector } from "react-redux";
import CustomSelect from "src/app/components/CustomSelect/CustomSelect";
import IDHFormattedMessage from "src/app/components/IDHFormattedMessage/IDHFormattedMessage";
import Modal, {
  ModalTitle,
  OutsideClickModes,
} from "src/app/components/Modal/Modal";
import { MetaFieldType } from "src/app/methods/getMetaFieldTypeOptions";
import { translateMessage } from "src/app/methods/translateMessage";
import { SelectOption } from "src/app/modals/CreateNewProjectPane/types";
import {
  createDictionaryElements,
  setDictionaryElementMetaValue,
} from "src/redux";
import { DictionaryList } from "src/redux/dictionary/types";
import { RootState } from "src/redux/reducers";
import { isStringUuid, sleep } from "src/utils/methods";
import { uuidv7 as uuid } from "uuidv7";
import { DictionaryItem } from "../../DictionaryTab";

import "./AssignDictionaryModal.scss";

interface AssignDictionaryModalProps {
  onClose: () => void;
  dictionaryData: DictionaryItem;
  assignedDictionaryElements: DictionaryList | null;
  allDictionaryElements: DictionaryList | null;
  getLists: () => void;
}

export default function AssignDictionaryModal({
  onClose,
  dictionaryData,
  assignedDictionaryElements,
  allDictionaryElements,
  getLists,
}: AssignDictionaryModalProps) {
  const [selectedDictionaries, setSelectedDictionaries] = useState<
    SelectOption[]
  >([]);
  const [loading, setLoading] = useState(false);

  const activeWorkspaceUuid = useSelector(
    (state: RootState) => state.mainReducer.activeWorkspaceUuid,
  );
  const globalTaskDetails = useSelector(
    (state: RootState) => state.creatorDatabaseReducer.globalTaskDetails,
  );

  const dispatch = useDispatch();

  const assignedDictionaryElementsIds =
    assignedDictionaryElements?.list.map((item) => item.uuid) ?? [];

  const filteredDictionaryElements =
    allDictionaryElements?.list.filter(
      (item) => !assignedDictionaryElementsIds.includes(item.uuid),
    ) ?? [];

  const options = filteredDictionaryElements?.map((item) => ({
    value: item.uuid,
    label: item.title,
    metadata: item.metadata,
  }));

  const intl = useIntl();

  const addNewElements = () => {
    const newElements = selectedDictionaries.filter(
      (selectedDictionary) => !isStringUuid(selectedDictionary.value),
    );
    if (!newElements.length) return;

    const elementsData = newElements.map((el) => {
      const elementUuid = uuid();

      return {
        name: el.label,
        uuid: elementUuid,
        defaultMetaFieldsValues: [
          {
            wsDictionaryMetaFieldType: MetaFieldType.GlobalCreator,
            value: [globalTaskDetails.uuid],
          },
        ],
      };
    });

    dispatch(
      createDictionaryElements(
        activeWorkspaceUuid,
        dictionaryData.wsDictionaryUuid,
        elementsData,
      ),
    );
  };

  const assignExistingElements = () => {
    const existingElementsIds = selectedDictionaries
      .filter((selectedDictionary) => isStringUuid(selectedDictionary.value))
      .map((selectedDictionary) => selectedDictionary.value);

    if (!existingElementsIds?.length) return;

    dispatch(
      setDictionaryElementMetaValue(
        existingElementsIds,
        dictionaryData.wsDictionaryGlobalCreatorMetaFieldUuid,
        globalTaskDetails.uuid,
        true,
      ),
    );
  };

  const onConfirmClick = async () => {
    setLoading(true);
    await addNewElements();
    await assignExistingElements();
    await sleep(300);
    setLoading(false);
    getLists();
    onClose();
  };

  return (
    <Modal
      onClose={onClose}
      className="assign-dictionary-modal"
      displayCancelButton
      closeButtonText={
        <IDHFormattedMessage id="ws_cancel" defaultMessage="Cancel" />
      }
      confirmButtonLoading={loading}
      confirmButtonDisabled={selectedDictionaries.length === 0}
      onConfirmClick={onConfirmClick}
      confirmButtonText={
        <IDHFormattedMessage id="ws_add" defaultMessage="Add" />
      }
      outsideClick={OutsideClickModes.Allow}
    >
      <ModalTitle>
        <IDHFormattedMessage id="ws_select_element" defaultMessage="Select" />{" "}
        {dictionaryData.wsDictionaryElementName}
      </ModalTitle>

      <CustomSelect
        value={selectedDictionaries}
        onChange={setSelectedDictionaries}
        options={options}
        isSearchable
        isMulti
        placeholder={translateMessage({
          intl,
          id: `ws_enter_name_or_select_element`,
          defaultMessage: "Enter a name or select an element",
        })}
      />
    </Modal>
  );
}
